import React, { useMemo } from "react";
import SkeletonTicketList from "../../../loaders/SkeletonTicketList";
import { useFirstAidManagerOverview, useOhsBuildingCheckList } from "../../../query/hooks/QueryHooksFirstAidBox";
import BoxSummaryCard from "./BoxSummaryCard";
import BuildingSummaryCard from "./BuildingSummaryCard";
import TagMismatches from "./TagMismatches";
import {
    CampusGroupTitle,
    EmptyState,
    MutedText,
    SectionCard,
    SectionHeader,
    SectionTitle,
    StatLabel,
    StatTile,
    StatTileGrid,
    StatValue,
    TwoColumnGrid
} from "./Ohs.styles";


/**
 * Manager overview - the stat tiles, tag mismatches awaiting acknowledgement, the boxes needing
 * attention, the buildings due a check this month or whose last check found issues, and every box by campus.
 */
function ManagerOverview() {
    const { data: overview, isLoading } = useFirstAidManagerOverview();
    const { data: buildingChecks, isLoading: isBuildingChecksLoading } = useOhsBuildingCheckList();
    const dueBuildings = useMemo(() => buildingChecks.filter((building) => !building.is_checked_this_month), [buildingChecks]);
    // A building is non-compliant while its latest check has any issue (an answer other than the expected one).
    const nonCompliantBuildings = useMemo(() => buildingChecks.filter((building) => building.last_issue_count > 0), [buildingChecks]);

    const boxesByCampus = useMemo(() => {
        const campusGroups = new Map();
        (overview?.boxes || []).forEach((box) => {
            const campusName = box.campus_name || "No campus";
            if (!campusGroups.has(campusName)) {
                campusGroups.set(campusName, []);
            }
            campusGroups.get(campusName).push(box);
        });
        return [...campusGroups.entries()];
    }, [overview]);

    if (isLoading || isBuildingChecksLoading || !overview) {
        return <SkeletonTicketList />;
    }

    return (
        <>
            <StatTileGrid>
                <StatTile>
                    <StatValue>{overview.totalBoxes}</StatValue>
                    <StatLabel>Active boxes</StatLabel>
                </StatTile>
                <StatTile>
                    <StatValue $needsAttention={overview.notCheckedThisMonth > 0}>{overview.notCheckedThisMonth}</StatValue>
                    <StatLabel>Not checked this month</StatLabel>
                </StatTile>
                <StatTile>
                    <StatValue $needsAttention={overview.nonCompliant > 0}>{overview.nonCompliant}</StatValue>
                    <StatLabel>Non-compliant</StatLabel>
                    <MutedText>Low, damaged or expired items</MutedText>
                </StatTile>
                <StatTile>
                    <StatValue $needsAttention={overview.restockLines > 0}>{overview.restockLines}</StatValue>
                    <StatLabel>Items needing restock</StatLabel>
                    <MutedText>Across {overview.boxesNeedingRestock} {overview.boxesNeedingRestock === 1 ? "box" : "boxes"}</MutedText>
                </StatTile>
                <StatTile>
                    <StatValue $needsAttention={overview.tagMismatches.length > 0}>{overview.tagMismatches.length}</StatValue>
                    <StatLabel>Tag mismatches</StatLabel>
                    <MutedText>Waiting to be acknowledged</MutedText>
                </StatTile>
                <StatTile>
                    <StatValue $needsAttention={dueBuildings.length > 0}>{dueBuildings.length}</StatValue>
                    <StatLabel>Building checks due</StatLabel>
                    <MutedText>Of {buildingChecks.length} {buildingChecks.length === 1 ? "building" : "buildings"} this month</MutedText>
                </StatTile>
                <StatTile>
                    <StatValue $needsAttention={nonCompliantBuildings.length > 0}>{nonCompliantBuildings.length}</StatValue>
                    <StatLabel>Non-compliant buildings</StatLabel>
                    <MutedText>Issues found on their last check</MutedText>
                </StatTile>
            </StatTileGrid>

            {overview.tagMismatches.length > 0 && <TagMismatches tagMismatches={overview.tagMismatches} />}

            <TwoColumnGrid>
                <SectionCard>
                    <SectionHeader>
                        <SectionTitle>Not Checked This Month</SectionTitle>
                        <MutedText>{overview.overdueBoxes.length}</MutedText>
                    </SectionHeader>
                    {overview.overdueBoxes.length === 0 ? (
                        <EmptyState>Every box has been checked this month.</EmptyState>
                    ) : (
                        overview.overdueBoxes.map((box) => <BoxSummaryCard key={box.id} box={box} showCampus />)
                    )}
                </SectionCard>
                <SectionCard>
                    <SectionHeader>
                        <SectionTitle>Non-Compliant Boxes</SectionTitle>
                        <MutedText>{overview.nonCompliantBoxes.length}</MutedText>
                    </SectionHeader>
                    {overview.nonCompliantBoxes.length === 0 ? (
                        <EmptyState>No box reported low, damaged or expired items on its last check.</EmptyState>
                    ) : (
                        overview.nonCompliantBoxes.map((box) => <BoxSummaryCard key={box.id} box={box} showCampus />)
                    )}
                </SectionCard>
            </TwoColumnGrid>

            <TwoColumnGrid>
                <SectionCard>
                    <SectionHeader>
                        <SectionTitle>Building Checks Due This Month</SectionTitle>
                        <MutedText>{dueBuildings.length}</MutedText>
                    </SectionHeader>
                    {dueBuildings.length === 0 ? (
                        <EmptyState>
                            {buildingChecks.length
                                ? "Every building has been checked this month."
                                : "No buildings have OHS Representatives assigned yet - add them on the Building Checks tab."}
                        </EmptyState>
                    ) : (
                        dueBuildings.map((building) => <BuildingSummaryCard key={building.building_id} building={building} />)
                    )}
                </SectionCard>
                <SectionCard>
                    <SectionHeader>
                        <SectionTitle>Non-Compliant Buildings</SectionTitle>
                        <MutedText>{nonCompliantBuildings.length}</MutedText>
                    </SectionHeader>
                    {nonCompliantBuildings.length === 0 ? (
                        <EmptyState>No building&apos;s last check found any issues.</EmptyState>
                    ) : (
                        nonCompliantBuildings.map((building) => <BuildingSummaryCard key={building.building_id} building={building} />)
                    )}
                </SectionCard>
            </TwoColumnGrid>

            <SectionCard>
                <SectionHeader>
                    <SectionTitle>All Boxes</SectionTitle>
                </SectionHeader>
                {boxesByCampus.length === 0 ? (
                    <EmptyState>No active boxes yet.</EmptyState>
                ) : (
                    boxesByCampus.map(([campusName, campusBoxes]) => (
                        <div key={campusName}>
                            <CampusGroupTitle>{campusName}</CampusGroupTitle>
                            {campusBoxes.map((box) => <BoxSummaryCard key={box.id} box={box} />)}
                        </div>
                    ))
                )}
            </SectionCard>
        </>
    );
}

export default ManagerOverview;
